import React from 'react';
import { useNavigate } from 'react-router-dom';

const pillars = [
  {
    icon: 'fa-building',
    title: 'Startups & SMEs',
    desc: 'Empowering early-stage startups and established small-to-medium enterprises with structured legal guidance and incorporation services.',
  },
  {
    icon: 'fa-chart-pie',
    title: 'Years of Proven Expertise',
    desc: 'A dynamic, multi-disciplinary team of compliance professionals, certified lawyers, and CAs working together to manage regulatory filings.',
  },
  {
    icon: 'fa-laptop-file',
    title: 'Technology-Driven Support',
    desc: 'We utilize automated compliance tracking and secure data channels to provide error-free, timely registrations across India.',
  },
];

const WhoWeAre = () => {
  const navigate = useNavigate();

  return (
    <section id="about" className="section-padding bg-white">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        .wwa-section {
          font-family: 'Inter', sans-serif;
        }

        /* ─── Two-column row ─── */
        .wwa-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 72px;
          align-items: center;
          margin-bottom: 72px;
        }
        @media (max-width: 991px) {
          .wwa-row { grid-template-columns: 1fr; gap: 48px; margin-bottom: 48px; }
        }

        /* ─── Image column ─── */
        .wwa-img-col {
          position: relative;
        }

        .wwa-img-frame {
          border-radius: 28px;
          overflow: hidden;
          position: relative;
          aspect-ratio: 4/5;
          max-height: 520px;
        }

        .wwa-img-frame img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          border-radius: 28px;
        }

        /* Soft gradient overlay at bottom */
        .wwa-img-frame::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(6, 24, 48, 0.18) 0%, transparent 50%);
          border-radius: 28px;
          pointer-events: none;
        }

        /* Floating stat badge bottom-left */
        .wwa-badge-bl {
          position: absolute;
          bottom: -20px;
          left: -20px;
          background: #ffffff;
          border-radius: 18px;
          padding: 18px 22px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.08);
          border: 1px solid #f0f4f8;
          display: flex;
          align-items: center;
          gap: 14px;
          z-index: 2;
        }

        .wwa-badge-icon {
          width: 46px; height: 46px;
          border-radius: 13px;
          background: #ffffff;
          border: 1.5px solid rgba(207, 160, 71, 0.3);
          display: flex; align-items: center; justify-content: center;
          font-size: 18px;
          color: #061830;
          flex-shrink: 0;
        }

        .wwa-badge-val {
          font-size: 26px;
          font-weight: 800;
          color: #0f172a;
          line-height: 1;
          letter-spacing: -0.5px;
        }

        .wwa-badge-lbl {
          font-size: 12px;
          color: #64748b;
          margin-top: 3px;
          font-weight: 500;
        }

        /* Floating experience badge top-right */
        .wwa-badge-tr {
          position: absolute;
          top: 24px;
          right: -20px;
          background: #061830;
          border-radius: 16px;
          padding: 16px 20px;
          box-shadow: 0 8px 24px rgba(6, 24, 48, 0.25);
          z-index: 2;
          text-align: center;
        }

        .wwa-badge-tr-num {
          font-size: 30px;
          font-weight: 800;
          color: #fff;
          line-height: 1;
          letter-spacing: -1px;
          display: block;
        }

        .wwa-badge-tr-lbl {
          font-size: 11px;
          color: rgba(255,255,255,0.7);
          font-weight: 500;
          margin-top: 4px;
          display: block;
        }

        /* ─── Text column ─── */
        .wwa-text-col { text-align: left; }

        .wwa-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #061830;
          font-size: 13px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1.4px;
          margin-bottom: 16px;
        }

        .wwa-eyebrow::before {
          content: '';
          display: block;
          width: 28px; height: 2px;
          background: #061830;
          border-radius: 2px;
        }

        .wwa-title {
          font-size: 42px;
          font-weight: 800;
          color: #0f172a;
          letter-spacing: -1.5px;
          line-height: 1.15;
          margin: 0 0 20px;
        }

        .wwa-title em {
          font-style: normal;
          color: #061830;
        }

        .wwa-desc {
          font-size: 15px;
          color: #475569;
          line-height: 1.8;
          margin-bottom: 24px;
        }

        .wwa-more-text {
          max-height: 0;
          opacity: 0;
          overflow: hidden;
          transition: max-height 0.4s ease, opacity 0.4s ease;
          margin-bottom: 24px;
        }

        .wwa-more-text.expanded {
          max-height: 250px;
          opacity: 1;
        }

        /* Checklist */
        .wwa-checks {
          display: flex;
          flex-direction: column;
          gap: 14px;
          margin-bottom: 36px;
        }

        .wwa-check-item {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 14.5px;
          color: #334155;
          font-weight: 500;
        }

        .wwa-check-dot {
          width: 22px; height: 22px;
          border-radius: 50%;
          background: #ffffff;
          border: 1.5px solid rgba(207, 160, 71, 0.4);
          display: flex; align-items: center; justify-content: center;
          color: #061830;
          font-size: 11px;
          flex-shrink: 0;
        }

        /* Red Read More Button as requested */
        .wwa-read-more-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 24px;
          background: #CFA047;
          color: white;
          border-radius: 50px;
          font-size: 13.5px;
          font-weight: 700;
          border: none;
          text-decoration: none;
          transition: background-color 0.2s, transform 0.2s, box-shadow 0.2s;
          box-shadow: 0 4px 10px rgba(207, 160, 71, 0.2);
          cursor: pointer;
        }

        .wwa-read-more-btn:hover {
          background-color: #a67f35;
          transform: translateY(-1px);
          box-shadow: 0 6px 14px rgba(207, 160, 71, 0.3);
          color: white;
        }

        /* ─── Pillar cards ─── */
        .wwa-pillars {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }
        @media (max-width: 991px) { .wwa-pillars { grid-template-columns: 1fr; } }

        .wwa-pillar {
          background: #f8fafc;
          border: 1.5px solid #e8edf3;
          border-radius: 20px;
          padding: 30px 26px;
          transition: transform 0.22s, box-shadow 0.22s, border-color 0.22s, background 0.22s;
          cursor: default;
          position: relative;
          overflow: hidden;
          text-align: left;
        }

        .wwa-pillar::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 3px;
          background: linear-gradient(90deg, #061830, #CFA047);
          opacity: 0;
          transition: opacity 0.22s;
          border-radius: 20px 20px 0 0;
        }

        .wwa-pillar:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 36px rgba(6, 24, 48, 0.08);
          border-color: rgba(6, 24, 48, 0.15);
          background: #fff;
        }

        .wwa-pillar:hover::before { opacity: 1; }

        .wwa-pillar-icon {
          width: 50px; height: 50px;
          border-radius: 14px;
          background: #ffffff;
          border: 1.5px solid rgba(207, 160, 71, 0.3);
          display: flex; align-items: center; justify-content: center;
          font-size: 20px;
          color: #061830;
          margin-bottom: 18px;
          transition: background 0.22s, color 0.22s;
        }

        .wwa-pillar:hover .wwa-pillar-icon {
          background: #061830;
          color: white;
          border-color: #061830;
        }

        .wwa-pillar-title {
          font-size: 16px;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 10px;
          line-height: 1.35;
        }

        .wwa-pillar-desc {
          font-size: 13.5px;
          color: #64748b;
          line-height: 1.75;
          margin: 0;
        }
      `}</style>

      <div className="container wwa-section">

        {/* ─── Two-column row ─── */}
        <div className="wwa-row">

          {/* Image column */}
          <div className="wwa-img-col">
            <div className="wwa-img-frame">
              <img
                src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=800&q=80"
                alt="Legal Infos professional team"
              />
            </div>

            {/* Top-right green badge */}
            <div className="wwa-badge-tr">
              <span className="wwa-badge-tr-num">12+</span>
              <span className="wwa-badge-tr-lbl">Years of<br/>Experience</span>
            </div>

            {/* Bottom-left stat badge */}
            <div className="wwa-badge-bl">
              <div className="wwa-badge-icon">
                <i className="fa-solid fa-face-smile"></i>
              </div>
              <div>
                <div className="wwa-badge-val">5 Lakh+</div>
                <div className="wwa-badge-lbl">Happy Clients</div>
              </div>
            </div>
          </div>

          {/* Text column */}
          <div className="wwa-text-col">
            <div className="wwa-eyebrow">About Legal Infos</div>

            <h2 className="wwa-title">
              About <em>Legal Infos</em>
            </h2>

            <p className="wwa-desc">
              Legal Infos is a professional legal and business compliance consultancy helping
              entrepreneurs, startups, SMEs, traders, manufacturers, and growing businesses across
              India.
            </p>

            <p className="wwa-desc">
              From company incorporation and trademark registration to GST, FSSAI, IEC, and ISO
              certification, we provide comprehensive compliance solutions under one roof. Our mission is
              to simplify business registrations and regulatory requirements so entrepreneurs can focus on
              growth while we handle the compliance.
            </p>

            <div className="wwa-more-text expanded">
              <p className="wwa-desc">
                We believe that starting a business should be exciting, not bogged down by confusing legal
                requirements. Our automated software integrations, coupled with expert advisory from CAs,
                CSs, and lawyers, provide you with state-of-the-art transparency, reliability, and peace of mind.
              </p>
              <div className="wwa-checks">
                {[
                  'DPIIT recognized Startup Facilitators',
                  'ISO 9001:2015 Quality Assured',
                  'Secure, Confidential & Encrypted Cloud Storage',
                ].map((item, i) => (
                  <div key={i} className="wwa-check-item">
                    <div className="wwa-check-dot">
                      <i className="fa-solid fa-check"></i>
                    </div>
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <button 
              className="wwa-read-more-btn"
              onClick={() => navigate('/about')}
            >
              Read More <i className="fa-solid fa-arrow-right ms-1"></i>
            </button>
          </div>
        </div>

        {/* ─── Three pillar cards ─── */}
        <div className="wwa-pillars">
          {pillars.map((p, i) => (
            <div key={i} className="wwa-pillar">
              <div className="wwa-pillar-icon">
                <i className={`fa-solid ${p.icon}`}></i>
              </div>
              <div className="wwa-pillar-title">{p.title}</div>
              <p className="wwa-pillar-desc">{p.desc}</p>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default WhoWeAre;
